import _ from 'lodash';
import { TYPE_BEAT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { CONFIG_ECG } from '@/entities/ecg';
import { isSameBeats } from '@/shared/lib';
import { findInBulkChanges, generateBeatTypeString } from '@/entities/beat';

export const LIMIT_BEATS_UPDATE = 5000;

/**
 * Builds the per-beat objects a beat strip needs from the parallel arrays the beat API returns. Walks the beat id list and pairs each index with its status, channel, heat-map cell, reviewed flag and template id, deriving both type and originalType through generateBeatTypeString and treating a reviewed value of 1 as reviewed.
 *
 * @param beats - Beat identifiers, one per beat, whose index drives the lookup into every other array.
 * @param hesBeatStatus - Raw HES status codes converted into the beat type strings.
 * @param beatChannels - Channel index each beat was detected on.
 * @param beatHeatMapIds - Heat-map cell id each beat belongs to, when the heat map is loaded.
 * @param beatReviewed - Review flags where 1 marks the beat as already reviewed.
 * @param beatTemplateIds - Template id each beat is currently assigned to.
 * @returns One object per beat carrying id, type, channel, cellId, originalType, isReviewed, originalReviewed, originalTemplateId and templateId.
 */
export const formatBeatStripRr = (
  beats: any[],
  hesBeatStatus: any[],
  beatChannels: any[],
  beatHeatMapIds?: any[],
  beatReviewed?: any[],
  beatTemplateIds?: any[],
) =>
  _.map(beats, (d, index) => {
    const templateId = beatTemplateIds?.[index];
    const isReviewed = beatReviewed?.[index] === 1;
    return {
      id: d,
      type: generateBeatTypeString(hesBeatStatus[index]),
      channel: beatChannels[index],
      cellId: beatHeatMapIds?.[index],
      originalType: generateBeatTypeString(hesBeatStatus[index]),
      isReviewed,
      originalReviewed: isReviewed,
      originalTemplateId: templateId,
      templateId,
    };
  });

// export const updateListBeatReview = (beats, stripSelect, isReviewed) => {
//   const data = _.cloneDeep(beats);
//   // Case toggle review
//   if (stripSelect.isReviewed === isReviewed) {
//     _.remove(data, (x) => {
//       if (x.id === stripSelect?.id) {
//         // If dont have type beat => have not change to Artifact or Delete => remove from changes array
//         if (_.isNil(x.type)) {
//           return true;
//         }
//         // Assign review state to keep changed type
//         _.assign(x, { isReviewed });
//         return false;
//       }
//       return false;
//     });
//     return data;
//   }
//   const ele = _.find(data, x => x.id === stripSelect?.id);
//   // Event has changed
//   if (ele) {
//     if (isReviewed === ele.oldReviewed) {
//       _.remove(data, (x) => {
//         if (x.id === stripSelect?.id) {
//           // If dont have type beat => have not change to Artifact or Delete => remove from changes array
//           if (_.isNil(x.type)) {
//             return true;
//           }
//           // Assign review state to keep changed type beat
//           _.assign(x, { isReviewed });
//           return false;
//         }
//         return false;
//       });
//       return data;
//     }
//     // For multiple select and mark review
//     if (isReviewed === ele.isReviewed) {
//       return data;
//     }
//     _.assign(ele, { isReviewed, oldReviewed: ele.oldReviewed || ele.isReviewed });
//   } else {
//     data.push({
//       ...stripSelect,
//       ...{
//         isReviewed,
//         oldReviewed: stripSelect?.isReviewed,
//         oldType: stripSelect.type,
//       },
//     });
//   }
//   return data;
// };

// export const updateListBeatChange = (beats, stripSelect, type) => {
//   const data = _.cloneDeep(beats);
//   // case beat orginal type === update type => remove if in beat change
//   if (stripSelect.type === type) {
//     _.remove(data, x => x.id === stripSelect?.id);
//     return data;
//   }
//   const ele = _.find(data, x => x.id === stripSelect?.id);
//   if (ele) {
//     if (type === ele.oldType) {
//       // case beat has changed and update to new type === original type
//       _.remove(data, x => x.id === stripSelect?.id);
//       return data;
//     }
//     if (type === ele.type) {
//       // case beat has changed and update to new type === current type
//       return data;
//     }
//     // case beat has changed and update to new type !== original type
//     _.assign(ele, { type, oldType: ele.oldType || ele.type });
//   } else {
//     data.push({
//       ...stripSelect,
//       ...{
//         type,
//         oldType: stripSelect.type,
//         isReviewed: stripSelect?.isReviewed,
//         oldReviewed: stripSelect?.isReviewed,
//       },
//     });
//   }
//   return data;
// };

/**
 * Diffs the current beat markers against the previous ones to build the Add, Remove and Edit records saved with a beat edit. Markers only in the current set become Add, markers only in the previous set become Remove carrying their original type and heat-map cell, and markers in both are compared with isSameBeats so changed ones become Edit; each sample offset is turned into an epoch time using the ECG sampling frequency.
 *
 * @param currentBeatMarkers - Beat sample offsets after the edit.
 * @param prevBeatMarkers - Beat sample offsets before the edit.
 * @param currentHesBeatStatus - HES status codes aligned with the current markers.
 * @param prevHesBeatStatus - HES status codes aligned with the previous markers.
 * @param originalHesBeatStatus - HES status codes as first loaded, used for the oldType of removed and edited beats.
 * @param startEpoch - Epoch of the first sample, the base for converting offsets to times.
 * @param beatHeatMapIds - Heat-map cell ids indexed by the original marker positions.
 * @param originalBeatMarker - Beat sample offsets as first loaded, used to index the heat-map cells.
 * @returns The Add, Remove and Edit beat-marker records describing the change.
 */
export const getUpdatedBeatMarkerV2 = (
  currentBeatMarkers: any[],
  prevBeatMarkers: any[],
  currentHesBeatStatus: any[],
  prevHesBeatStatus: any[],
  originalHesBeatStatus: any[],
  startEpoch: number,
  beatHeatMapIds: any[] = [],
  originalBeatMarker: any[],
) => {
  const updatedBeatMarkers: any[] = [];
  // *: Has at current but not previous
  const beatMarkersAdded = _.difference(currentBeatMarkers, prevBeatMarkers);
  _.forEach(beatMarkersAdded, (item: any) => {
    const currentBeatIndex = _.findIndex(currentBeatMarkers, (x) => x === item);
    updatedBeatMarkers.push({
      time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      type: generateBeatTypeString(currentHesBeatStatus[currentBeatIndex]),
      actionType: 'Add',
    });
  });

  // *: Has at previous but not current
  const beatMarkersRemoved = _.difference(prevBeatMarkers, currentBeatMarkers);
  _.forEach(beatMarkersRemoved, (item: any) => {
    const prevBeatIndex = _.findIndex(prevBeatMarkers, (x) => x === item);
    const originalBeatIndex = _.findIndex(originalBeatMarker, (x) => x === item);
    updatedBeatMarkers.push({
      time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      id: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      type: TYPE_BEAT_ENUM.DELETED,
      oldType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
      actionType: 'Remove',
      cellId: beatHeatMapIds[originalBeatIndex],
    });
  });

  // *: Has both
  const beatMarkersIntersection = _.intersection(prevBeatMarkers, currentBeatMarkers);
  _.forEach(beatMarkersIntersection, (item: any) => {
    // *: Compare hesBeatStatus in currentHesBeatStatus (with currentIndex) to originalHesBeatStatus (with originalIndex)
    const currentBeatIndex = _.findIndex(currentBeatMarkers, (x) => x === item);
    const prevBeatIndex = _.findIndex(prevBeatMarkers, (x) => x === item);
    const originalBeatIndex = _.findIndex(originalBeatMarker, (x) => x === item);
    if (!isSameBeats(currentHesBeatStatus[currentBeatIndex], prevHesBeatStatus[prevBeatIndex])) {
      updatedBeatMarkers.push({
        time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
        type: generateBeatTypeString(currentHesBeatStatus[currentBeatIndex]),
        oldType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
        actionType: 'Edit',
        cellId: beatHeatMapIds[originalBeatIndex],
        originalType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
        id: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      });
    }
  });

  return updatedBeatMarkers;
};

/**
 * Screens a heart-rate value against the plausible range the report accepts. Compares the rate to the fixed 25 and 300 beats-per-minute bounds.
 *
 * @param hr - Heart rate in beats per minute.
 * @returns True when the rate is below 25 or above 300.
 */
export const isOutOfRangeHr = (hr: number) => hr < 25 || hr > 300;

// Check for css when change
export const ID_DIV_STRIP_ARTIFACT = 'stripArtifactsBeatHr';
export const ID_DIV_STRIP_AFIB = 'stripAfibsBeatHr';
export const ID_DIV_STRIP_BEATS = 'stripBeatsBeatHr';

export const HR_MODAL_TYPE = {
  MIN_HR: 'MINHR',
  MAX_HR: 'MAXHR',
  AVG_HR: 'AVGHR',
};

/**
 * Collects the distinct calendar days spanned by a list of beat epochs. Converts every epoch into the study timezone, truncates it to the start of the day and de-duplicates the resulting ISO strings through a Set.
 *
 * @param beats - Beat epoch timestamps to group by day.
 * @param timezoneOffset - Offset applied before truncating, so days align with the study timezone.
 * @returns The unique day-start ISO strings covered by the beats.
 */
export const getListBeatTime = (beats: any[] = [], timezoneOffset: number = 0) => {
  const uniqueDate = new Set();
  beats.forEach((epoch: any) => {
    uniqueDate.add(convertToMomentJs(epoch, timezoneOffset)?.startOf('days')?.toISOString());
  });
  return Array.from(uniqueDate);
};

export const handleResetAppliedBuldBeats = ({
  appliedBuldBeats = [] as any[],
  strips = [] as any[],
  type,
  bulkBeatsChanges,
  isClickReset,
}: {
  appliedBuldBeats?: any[];
  strips?: any[];
  type: any;
  bulkBeatsChanges: any;
  isClickReset?: any;
}) => {
  const newAppliedBuldBeats: any[] = _.cloneDeep(appliedBuldBeats);
  _.forEach(strips, (strip: any) => {
    const index = _.findIndex(newAppliedBuldBeats, (x: any) => x.id === strip.id);
    const isInBuldCells = findInBulkChanges(bulkBeatsChanges, strip);
    const isReset =
      (!type || strip?.originalType === type || strip?.oldType === type) && !!isInBuldCells;
    if (isReset || isClickReset) {
      newAppliedBuldBeats.push(strip);
    } else if (index > -1) {
      newAppliedBuldBeats.splice(index, 1);
    }
  });
  return _.uniqBy(newAppliedBuldBeats, 'id');
};
